import React, { useEffect, useRef } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useAuth } from "hooks";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";
import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import {
  useUpdateRouteStopMutation,
  useAddRouteStopMutation,
} from "store/services/routeShipmentApi"; // Add these

const INITIAL_STATE = {
  account: null,
  entity: null,
  origin: null,
  destination: null,
  distance: "",
  time: "",
  cost: "",
};

const RouteShipmentStopForm = ({
  isBusy,
  stop,
  previousStop,
  shipmentId,
  onSubmit,
  onCancel,
}) => {
  const { isAccountAdmin, isSystemAdmin, viewEntity } = useAuth();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();
  const [fetchEntities, { data: entities = [], isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [
    fetchBranches,
    { data: branchesRes = [], isLoading: loadingBranches },
  ] = useLazyGetDDLBranchListQuery();
  const [updateRouteStop] = useUpdateRouteStopMutation(); // Mutation for updating
  const [createRouteStop] = useAddRouteStopMutation(); // Mutation for creating

  useEffect(() => {
    if (isAccountAdmin || isSystemAdmin) {
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id,
      });
    }
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account?._id,
  ]);

  useEffect(() => {
    if (_watchEntity?._id) {
      fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
    }
  }, [_watchEntity?._id, _watchAccount?._id, fetchBranches]);

  const hasResetRef = useRef(false);
  useEffect(() => {
    if (!branchesRes || !branchesRes.length || hasResetRef.current) return;

    const mappedOrigin = previousStop
      ? branchesRes.find((b) => b._id === previousStop?.stopDestination?._id)
      : null;

    if (stop) {
      reset({
        account: stop.account || viewEntity?.entity?.account,
        entity: stop.entity || viewEntity?.entity,
        origin: branchesRes.find((b) => b._id === stop.stopOrigin?._id) || null,
        destination:
          branchesRes.find((b) => b._id === stop.stopDestination?._id) || null,
        distance: stop.stopDistance || "",
        time: stop.stopTime || "",
        cost: stop.stopCost || "",
      });
    } else if (previousStop && mappedOrigin) {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity,
        account: viewEntity?.entity?.account,
        origin: mappedOrigin,
      });
    } else {
      reset({
        ...INITIAL_STATE,
        entity: viewEntity?.entity,
        account: viewEntity?.entity?.account,
      });
    }

    hasResetRef.current = true;
  }, [stop, previousStop, branchesRes, reset, viewEntity?.entity]);

  const validateNumeric = (val) => {
    if (val === "" || isNaN(val))
      return "This field is required and must be a number.";
    return true;
  };

  const handleFormSubmit = async (formValues) => {
    if (!formValues.origin || !formValues.destination) {
      toast.error("Both origin and destination are required!");
      return;
    }

    if (formValues.origin._id === formValues.destination._id) {
      toast.error("Origin and destination cannot be the same!");
      return;
    }

    const payload = {
      stopOrigin: formValues.origin._id,
      stopDestination: formValues.destination._id,
      stopDistance: parseFloat(formValues.distance),
      stopTime: parseFloat(formValues.time),
      stopCost: parseFloat(formValues.cost),
    };

    try {
      if (stop?._id) {
        await updateRouteStop({
          shipmentId,
          stopId: stop._id,
          ...payload,
        }).unwrap();
        toast.success("Route stop updated successfully!");
      } else {
        await createRouteStop({
          shipmentId,
          ...payload,
        }).unwrap();
        toast.success("Route stop created successfully!");
      }

      // Reset the form
      reset(INITIAL_STATE);

      // Call onSubmit to notify parent of changes
      onSubmit(payload);

      // Optionally call onCancel to close the form
      if (onCancel) onCancel();
    } catch (error) {
      toast.error("Failed to save route stop!");
      console.error(error);
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={2}>
          {(isSystemAdmin || isAccountAdmin) && (
            <>
              <Grid item md={12}>
                <Controller
                  name="account"
                  control={control}
                  rules={{ required: "Please select an account." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Account"
                      required
                      disabled
                      value={field.value ?? ""}
                      onChange={(_, data) => {
                        if (data?._id !== field.value?._id) {
                          field.onChange(data);
                          setValue("entity", "");
                          setValue("origin", "");
                          setValue("destination", "");
                        }
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={accounts}
                      loading={loadingAccounts}
                      helperText={errors?.account?.message}
                      error={!!errors?.account}
                    />
                  )}
                />
              </Grid>

              <Grid item md={12}>
                <Controller
                  name="entity"
                  control={control}
                  rules={{ required: "Please select an entity." }}
                  render={({ field }) => (
                    <BaseAutocomplete
                      {...field}
                      fullWidth
                      label="Entity"
                      required
                      disabled
                      value={field.value ?? ""}
                      onChange={(_, data) => {
                        field.onChange(data);
                        setValue("origin", "");
                        setValue("destination", "");
                      }}
                      getOptionLabel={(option) => option?.name ?? ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      options={entities}
                      loading={loadingEntities}
                      helperText={errors?.entity?.message}
                      error={!!errors?.entity}
                    />
                  )}
                />
              </Grid>
            </>
          )}

          <Grid item md={12}>
            <Controller
              name="origin"
              control={control}
              rules={{ required: "Please select source branch." }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Source Branch"
                  required
                  value={value}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option.name} - ${option.code}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branchesRes || []}
                  loading={loadingBranches}
                  helperText={error?.message}
                  error={!!error}
                  disabled={!stop && !!previousStop} // disable only when adding after another
                />
              )}
            />
          </Grid>

          <Grid item md={12}>
            <Controller
              name="destination"
              control={control}
              rules={{ required: "Please select destination branch." }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Destination Branch"
                  required
                  value={value}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option ? `${option.name} - ${option.code}` : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branchesRes || []}
                  loading={loadingBranches}
                  helperText={error?.message}
                  error={!!error}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} key="distance">
            <Controller
              name="distance"
              control={control}
              rules={{
                required: "Distance is required.",
                validate: validateNumeric,
              }}
              render={({ field: textField, fieldState: { error } }) => (
                <TextField
                  required
                  {...textField}
                  label="Distance"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} key="time">
            <Controller
              name="time"
              control={control}
              rules={{
                validate: (val) =>
                  val !== "" && isNaN(val) ? "Must be a number." : true,
              }}
              render={({ field: textField, fieldState: { error } }) => (
                <TextField
                  {...textField}
                  label="Time (in mins)"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} key="cost">
            <Controller
              name="cost"
              control={control}
              rules={{
                validate: (val) =>
                  val !== "" && isNaN(val) ? "Must be a number." : true,
              }}
              render={({ field: textField, fieldState: { error } }) => (
                <TextField
                  {...textField}
                  label="Cost"
                  type="number"
                  fullWidth
                  size="small"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={onCancel} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isBusy}
        >
          {stop ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default RouteShipmentStopForm;
